import { roundCurrency } from './currency.js';

// A proposal can be invoiced a few items at a time (Convert to Invoice). Each
// invoiced item stays on the proposal, stamped with the invoice it moved to —
// the proposal's total never changes, but those items are billed on the invoice
// and drop out of what's still owed on the proposal. Mirrors
// backend/src/utils/proposalInvoicing.js.

export function isInvoicedLineItem(item) {
  return Boolean(item?.convertedInvoiceId);
}

function hasInvoicedItems(proposal) {
  return (Array.isArray(proposal?.lineItems) ? proposal.lineItems : []).some(isInvoicedLineItem);
}

// Proposals converted before items could be invoiced separately went onto one
// invoice whole and carry no per-item markers; the proposals list flags them
// (`invoiced`) from the invoices that still exist.
function isLegacyInvoiced(proposal) {
  return Boolean(proposal?.invoiced) && !hasInvoicedItems(proposal);
}

// 'invoiced' | 'partially_invoiced' | null
export function getProposalInvoicingStatus(proposal) {
  if (isLegacyInvoiced(proposal)) return 'invoiced';
  const items = Array.isArray(proposal?.lineItems) ? proposal.lineItems : [];
  const invoicedCount = items.filter(isInvoicedLineItem).length;
  if (!invoicedCount) return null;
  return invoicedCount === items.length ? 'invoiced' : 'partially_invoiced';
}

export function getProposalInvoicedAmount(proposal) {
  return roundCurrency(proposal?.totals?.invoiced || 0);
}

// What the client still owes on the proposal itself: its total, less what's
// been invoiced and what's been paid on it (and not yet applied to an invoice).
// An archived proposal is kept for reference and owes nothing.
export function getProposalBalanceDue(proposal) {
  if (isLegacyInvoiced(proposal) || proposal?.status === 'archived') return 0;
  const total = Number(proposal?.totals?.grandTotal ?? proposal?.totals?.total ?? 0) || 0;
  const paid = Number(proposal?.totals?.paid || 0);
  return roundCurrency(Math.max(total - getProposalInvoicedAmount(proposal) - paid, 0));
}
